import React from 'react'
import PageHeader from '../components/PageHeader'
import SectionIntro from '../components/SectionIntro'
import Reveal, { MaskLines } from '../components/Reveal'
import { CountUp, Marquee } from '../components/Motion'
import Constellation from '../components/Constellation'
import { CallToAction } from './Home'
import { company, stats, policies, values, vision, mission, sectors } from '../data/site'

const About = () => (
  <main>
    <PageHeader
      index="01"
      label="About Aldaron"
      lines={[
        'A Kenyan partner',
        <>
          built for <span className="accent-italic">delivery</span>
        </>,
      ]}
      description={company.summary}
      crumb="About"
    />

    {/* ---------------- Who we are ---------------- */}
    <section className="section">
      <div className="container">
        <div className="grid gap-10 md:grid-cols-[10rem_1fr] md:gap-12">
          <Reveal className="flex items-baseline gap-4 pt-3">
            <span className="index-num">01</span>
            <span className="label">Who we are</span>
          </Reveal>

          <div>
            <MaskLines
              as="h2"
              className="display-2 max-w-4xl"
              lines={[
                'Technical expertise,',
                <>
                  creative <span className="accent-italic">insight</span>
                </>,
              ]}
            />

            <div className="mt-14 grid gap-14 lg:grid-cols-[1.1fr_0.9fr]">
              <Reveal delay={200} className="space-y-6 text-lg leading-relaxed text-ink-500">
                {company.about.map((para) => (
                  <p key={para.slice(0, 24)}>{para}</p>
                ))}
              </Reveal>

              <Reveal delay={320}>
                <dl className="divide-y divide-ink-950/10 border-y border-ink-950/10">
                  {[
                    ['Registration', 'Kenyan-registered company'],
                    ['Head office', company.location],
                    ['Sectors', sectors.join(', ')],
                    ['Routes to market', 'Direct supply, competitive tender'],
                    ['Practice areas', 'ICT, supply, branding, media'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between gap-6 py-5">
                      <dt className="label">{k}</dt>
                      <dd className="text-right font-display text-base font-medium text-ink-950">
                        {v}
                      </dd>
                    </div>
                  ))}
                </dl>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ---------------- Vision & mission ---------------- */}
    <section className="relative overflow-hidden bg-ink-950 bg-glow grain">
      <div className="pointer-events-none absolute -right-40 -top-32 hidden w-[38rem] opacity-70 lg:block">
        <Constellation className="w-full" />
      </div>

      <div className="container relative section">
        <SectionIntro
          index="02"
          label="Direction"
          tone="light"
          lines={[
            'Where we are going,',
            <>
              and how we <span className="accent-italic">get there</span>
            </>,
          ]}
        />

        <div className="mt-20 grid gap-14 md:grid-cols-2">
          {[
            { title: 'Vision', body: vision },
            { title: 'Mission', body: mission },
          ].map((block, i) => (
            <Reveal key={block.title} delay={i * 120}>
              <div className="border-t border-paper-100/15 pt-8">
                <span className="label label-dark">{block.title}</span>
                <p className="mt-8 font-display text-2xl font-normal leading-snug tracking-tighter2 text-paper-100 md:text-[1.75rem]">
                  {block.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <dl className="mt-24 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, i) => (
            <Reveal key={stat.label} delay={i * 90}>
              <div className="border-t border-paper-100/15 pt-6">
                <dt className="font-display text-5xl font-semibold tracking-tightest text-paper-100 md:text-6xl">
                  <CountUp value={stat.value} />
                </dt>
                <dd className="label label-dark mt-4">{stat.label}</dd>
              </div>
            </Reveal>
          ))}
        </dl>
      </div>
    </section>

    {/* ---------------- Values ticker ---------------- */}
    <section className="border-b border-ink-950/10 bg-paper-100 py-10">
      <Marquee speed="fast">
        {values.map((value) => (
          <span key={value} className="flex items-center">
            <span className="whitespace-nowrap px-8 font-display text-xl font-medium tracking-tighter2 text-ink-950/70">
              {value}
            </span>
            <span className="h-1 w-1 shrink-0 rounded-full bg-ochre" />
          </span>
        ))}
      </Marquee>
    </section>

    {/* ---------------- Policies ---------------- */}
    <section className="section">
      <div className="container">
        <SectionIntro
          index="03"
          label="Our policies"
          lines={[
            'How we hold',
            <>
              ourselves <span className="accent-italic">accountable</span>
            </>,
          ]}
          description="The commitments we bring to every client, tender, and procurement process."
        />

        <div className="mt-20">
          {policies.map((policy, i) => (
            <Reveal key={policy.title} delay={i * 90}>
              <article className="grid items-baseline gap-x-10 gap-y-4 border-t border-ink-950/10 py-10 last:border-b md:grid-cols-[4rem_1fr_1.1fr]">
                <span className="index-num">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="display-3 pr-6">{policy.title}</h3>
                <p className="text-ink-500">{policy.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CallToAction />
  </main>
)

export default About
